import { useMutation } from '@tanstack/react-query'
import type { FormEvent, ReactNode } from 'react'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { adminApi } from '@/api/endpoints'
import type {
  AdminReport,
  AdminUser,
  ModerationAction,
  ModerationActionKind,
  ReportAction,
  ReportStatus,
  UserBrief,
} from '@/api/types'
import { isDeleted } from '@/api/types'
import { PageHeader } from '@/app/layouts/PageHeader'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { EmptyState, ErrorState } from '@/components/ui/EmptyState'
import { PostListSkeleton } from '@/components/ui/Skeleton'
import { CenteredSpinner } from '@/components/ui/Spinner'
import { useCurrentUser } from '@/features/auth'
import {
  reasonLabel,
  useAdminActions,
  useAdminReports,
  useResolveReport,
  useSuspendToggle,
} from '@/features/moderation'
import { QuoteCard } from '@/features/posts'
import { useInfiniteScroll } from '@/hooks/useInfiniteScroll'
import { useT, useTx } from '@/i18n'
import type { MessageKey } from '@/i18n'
import { cn } from '@/lib/cn'
import { useFormat } from '@/lib/format'

type Tab = ReportStatus | 'actions'

const TABS: { key: Tab; label: MessageKey }[] = [
  { key: 'open', label: 'admin.tabOpen' },
  { key: 'resolved', label: 'admin.tabResolved' },
  { key: 'dismissed', label: 'admin.tabDismissed' },
  { key: 'actions', label: 'admin.tabActions' },
]

/**
 * 管理页（D66–D68）：举报队列 + 操作记录。
 *
 * 入口只对管理员显示，但页面本身也挡一下 —— 真正的权限在后端，
 * 这里只是让非管理员看到一句人话，而不是一串 403。
 */
export function AdminPage() {
  const t = useT()
  const { user } = useCurrentUser()
  const [tab, setTab] = useState<Tab>('open')

  if (!user?.is_admin) {
    return (
      <>
        <PageHeader title={t('admin.title')} />
        <EmptyState title={t('admin.forbidden')} />
      </>
    )
  }

  return (
    <>
      <PageHeader title={t('admin.title')}>
        <nav className="flex" aria-label={t('admin.tabsLabel')}>
          {TABS.map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}
              className={cn(
                'flex-1 py-4 text-[15px] transition-colors hover:bg-surface-hover',
                tab === key ? 'font-bold' : 'text-ink-muted',
              )}
            >
              <span
                className={cn('inline-block pb-3', tab === key && 'border-b-4 border-brand-ink')}
              >
                {t(label)}
              </span>
            </button>
          ))}
        </nav>
      </PageHeader>

      {tab === 'actions' ? (
        <>
          <AccountLookup />
          <ActionLog />
        </>
      ) : (
        <ReportQueue key={tab} status={tab} />
      )}
    </>
  )
}

// --------------------------------------------------------------------------- 举报队列

function ReportQueue({ status }: { status: ReportStatus }) {
  const t = useT()
  const query = useAdminReports(status)
  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  if (query.isPending) return <PostListSkeleton count={3} />
  if (query.isError)
    return <ErrorState message={query.error.message} onRetry={() => query.refetch()} />

  const reports = query.data.pages.flatMap((page) => page.items)
  if (reports.length === 0) {
    return status === 'open' ? (
      <EmptyState title={t('admin.emptyOpen')} description={t('admin.emptyOpenBody')} />
    ) : (
      <EmptyState title={t('admin.empty')} />
    )
  }

  return (
    <div>
      {reports.map((report) => (
        <ReportCard key={report.id} report={report} />
      ))}
      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </div>
  )
}

/** 备注只进操作记录。点了「取消」就是不处置了 */
function askNote(t: ReturnType<typeof useT>): string | null {
  const note = window.prompt(t('admin.notePrompt'), '')
  return note === null ? null : note.trim()
}

function ReportCard({ report }: { report: AdminReport }) {
  const t = useT()
  const tx = useTx()
  const { relativeTime } = useFormat()
  const resolve = useResolveReport()

  const act = (action: ReportAction) => {
    const note = askNote(t)
    if (note === null) return
    resolve.mutate({ id: report.id, action, note })
  }

  const post = report.post
  const postAlive = post !== null && !isDeleted(post)
  const target = report.target

  return (
    <article className="space-y-3 border-b border-line px-4 py-4">
      <header className="flex flex-wrap items-center gap-2">
        <span className="bg-danger/10 px-2 py-0.5 text-[13px] font-bold text-danger px-corners [--px-step:1px]">
          {t(reasonLabel(report.reason))}
        </span>
        {report.status === 'open' && report.open_on_target > 1 && (
          <span className="bg-surface-subtle px-2 py-0.5 text-[13px] font-bold text-ink-muted px-corners [--px-step:1px]">
            {t('admin.openOnTarget', { count: report.open_on_target })}
          </span>
        )}
        <span className="ml-auto text-[13px] text-ink-muted">
          {tx('admin.reportedBy', {
            user: <UserLink user={report.reporter} />,
            time: relativeTime(report.created_at),
          })}
        </span>
      </header>

      {report.note && (
        <blockquote className="post-text border-l-4 border-line-strong pl-3 text-[15px]">
          {report.note}
        </blockquote>
      )}

      {target && <TargetLine user={target} />}

      {post === null ? (
        <p className="text-[13px] text-ink-muted">{t('admin.userReport')}</p>
      ) : postAlive ? (
        <QuoteCard post={post} />
      ) : (
        // 帖子已经没了：原文另给一份，结案之后还看得到当初处置的是什么
        <div className="rounded-md border border-dashed border-line-strong px-3 py-2 text-[15px] text-ink-muted">
          <p className="text-[13px] font-bold">{t('admin.postGone')}</p>
          {report.post_content && <p className="post-text mt-1">{report.post_content}</p>}
        </div>
      )}

      {report.status === 'open' ? (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={resolve.isPending}
            onClick={() => act('dismiss')}
          >
            {t('admin.dismiss')}
          </Button>
          {postAlive && (
            <Button
              variant="danger"
              size="sm"
              disabled={resolve.isPending}
              onClick={() => act('remove_post')}
            >
              {t('admin.removePost')}
            </Button>
          )}
          {target && !target.suspended && !target.is_admin && (
            <Button
              variant="danger"
              size="sm"
              disabled={resolve.isPending}
              onClick={() => act('suspend_user')}
            >
              {t('admin.suspendUser')}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-[13px] text-ink-muted">
          <span className="font-bold text-ink">{resolutionLabel(report, t)}</span>
          {report.resolved_at && (
            <>
              {' · '}
              {tx('admin.resolvedBy', {
                user: <UserLink user={report.resolved_by} />,
                time: relativeTime(report.resolved_at),
              })}
            </>
          )}
        </p>
      )}
    </article>
  )
}

function resolutionLabel(report: AdminReport, t: ReturnType<typeof useT>): string {
  if (report.status === 'dismissed') return t('admin.resDismissed')
  return report.resolution === 'user_suspended'
    ? t('admin.resUserSuspended')
    : t('admin.resPostRemoved')
}

function UserLink({ user }: { user: UserBrief | null }): ReactNode {
  const t = useT()
  if (!user) return <span>{t('admin.byCli')}</span>
  return (
    <Link to={`/${user.username}`} className="font-bold text-brand-ink hover:underline">
      @{user.username}
    </Link>
  )
}

function StatusBadges({ user }: { user: AdminUser }) {
  const t = useT()
  return (
    <>
      {user.suspended && (
        <span className="bg-danger px-1.5 py-0.5 text-[11px] font-bold text-surface px-corners [--px-step:1px]">
          {t('admin.suspendedBadge')}
        </span>
      )}
      {user.deactivated && (
        <span className="bg-surface-subtle px-1.5 py-0.5 text-[11px] text-ink-muted px-corners [--px-step:1px]">
          {t('admin.deactivatedBadge')}
        </span>
      )}
      {user.is_admin && (
        <span className="bg-brand px-1.5 py-0.5 text-[11px] font-bold text-on-brand px-corners [--px-step:1px]">
          {t('admin.adminBadge')}
        </span>
      )}
    </>
  )
}

function TargetLine({ user }: { user: AdminUser }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[15px]">
      <Avatar src={user.avatar_url} alt={user.display_name} size="xs" className="size-6" />
      <span className="font-bold">{user.display_name}</span>
      {/* 被封禁或停用的账号主页是 404，链过去没有意义 */}
      {user.suspended || user.deactivated ? (
        <span className="font-mono text-[14px] text-ink-muted">@{user.username}</span>
      ) : (
        <Link
          to={`/${user.username}`}
          className="font-mono text-[14px] text-ink-muted hover:underline"
        >
          @{user.username}
        </Link>
      )}
      <StatusBadges user={user} />
    </div>
  )
}

// --------------------------------------------------------------------------- 按用户名处理

/**
 * 被封禁或停用的账号在站内搜不到、主页也是 404，解封只能从这里找。
 * 清理公网上的种子账号也走这里（或者服务器上的 `flask admin suspend`）。
 */
function AccountLookup() {
  const t = useT()
  const [username, setUsername] = useState('')
  const lookup = useMutation({ mutationFn: (name: string) => adminApi.user(name) })
  const suspend = useSuspendToggle()
  const found = lookup.data

  const submit = (event: FormEvent) => {
    event.preventDefault()
    const name = username.trim().replace(/^@/, '')
    if (name) lookup.mutate(name)
  }

  return (
    <section className="border-b border-line px-4 py-4">
      <h2 className="text-lg font-extrabold">{t('admin.lookupTitle')}</h2>
      <p className="mt-1 text-[13px] text-ink-muted">{t('admin.lookupHint')}</p>
      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input
          value={username}
          onChange={(event) => setUsername(event.target.value)}
          placeholder={t('admin.lookupPlaceholder')}
          aria-label={t('admin.lookupPlaceholder')}
          className="min-w-0 flex-1 rounded-md border border-line-strong bg-transparent px-3 py-1.5 font-mono text-[15px] outline-none focus:border-brand-ink"
        />
        <Button type="submit" variant="outline" loading={lookup.isPending}>
          {t('admin.lookup')}
        </Button>
      </form>

      {lookup.isError && (
        <p role="alert" className="mt-2 text-sm text-danger">
          {lookup.error instanceof ApiError && lookup.error.status === 404
            ? t('admin.userNotFound')
            : lookup.error.message}
        </p>
      )}

      {found && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <TargetLine user={found} />
          {!found.is_admin && (
            <Button
              size="sm"
              variant={found.suspended ? 'outline' : 'danger'}
              loading={suspend.isPending}
              onClick={() => {
                const note = askNote(t)
                if (note === null) return
                suspend.mutate(
                  { username: found.username, suspended: found.suspended, note },
                  // 按钮上的状态以服务端返回为准
                  { onSuccess: () => lookup.mutate(found.username) },
                )
              }}
            >
              {found.suspended ? t('admin.unsuspend') : t('admin.suspendUser')}
            </Button>
          )}
        </div>
      )}
    </section>
  )
}

// --------------------------------------------------------------------------- 操作记录

const ACTION_LABEL: Record<ModerationActionKind, MessageKey> = {
  remove_post: 'admin.actRemovePost',
  suspend_user: 'admin.actSuspend',
  unsuspend_user: 'admin.actUnsuspend',
  dismiss_report: 'admin.actDismiss',
}

function ActionLog() {
  const t = useT()
  const query = useAdminActions()
  const sentinelRef = useInfiniteScroll({
    hasNextPage: Boolean(query.hasNextPage),
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
  })

  if (query.isPending) return <PostListSkeleton count={3} />
  if (query.isError)
    return <ErrorState message={query.error.message} onRetry={() => query.refetch()} />

  const actions = query.data.pages.flatMap((page) => page.items)
  if (actions.length === 0) return <EmptyState title={t('admin.empty')} />

  return (
    <>
      <ul>
        {actions.map((action) => (
          <ActionRow key={action.id} action={action} />
        ))}
      </ul>
      <div ref={sentinelRef} aria-hidden className="h-px" />
      {query.isFetchingNextPage && <CenteredSpinner />}
    </>
  )
}

function ActionRow({ action }: { action: ModerationAction }) {
  const t = useT()
  const { relativeTime } = useFormat()
  return (
    <li className="space-y-1 border-b border-line px-4 py-3 text-[15px]">
      <p className="flex flex-wrap items-center gap-x-2">
        <span className="font-bold">{t(ACTION_LABEL[action.action])}</span>
        {action.user && (
          <span className="font-mono text-[14px] text-ink-muted">@{action.user.username}</span>
        )}
        {action.post_id && action.user && action.action !== 'remove_post' && (
          <Link
            to={`/${action.user.username}/status/${action.post_id}`}
            className="text-[13px] text-brand-ink hover:underline"
          >
            {t('admin.viewPost')}
          </Link>
        )}
      </p>
      <p className="text-[13px] text-ink-muted">
        <UserLink user={action.admin} /> · {relativeTime(action.created_at)}
      </p>
      {action.note && <p className="post-text text-[13px] text-ink-muted">{action.note}</p>}
    </li>
  )
}
